<template>
  <div
    class="background wt-rtp-player"
    ref="background"
    :class="{ dragging: dragging }"
  >
    <div class="scale">
      <span
        class="wticon wticon-minus1"
        data-gap="-0.05"
        @click="scaleGap"
        v-longpress="scaleGap"
      ></span>
      <input
        type="range"
        name="scale"
        ref="scale"
        min="1"
        @click="inputScale"
        max="3"
        step="0.05"
        v-model="curScale"
      />
      <span
        class="wticon wticon-plus"
        data-gap="0.05"
        @click="scaleGap"
        v-longpress="scaleGap"
      ></span>
    </div>
    <div ref="rcp" class="rcp" @mousedown="startDrag" @mouseup="endDrag">
      <canvas for="player"></canvas>
      <canvas for="movie-processor"></canvas>
      <canvas for="diagnostic"></canvas>
      <div for="buffer-indicator">
        <img
          src="https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/preloader.svg"
        />
      </div>
      <img
        for="pause-indicator"
        alt="Play"
        src="/resources/img/play-icon-02.svg"
      />
      <img
        for="play-indicator"
        alt="Pause"
        src="/resources/img/pause-icon-02.svg"
      />
      <div
        class="overlay"
        :class="{
          active: focusAsset.name === 'overlay',
          disabled: !(overlay && overlay.metadata.url) || disableOverlay,
        }"
        v-if="overlay"
      ></div>
      <div
        class="main"
        :class="{
          active: focusAsset.name === 'main',
          disabled: !(main && main.metadata.url) || disableMain,
        }"
      ></div>
      <div ref="rcpPanel"></div>
      <!-- stage curtains -->
      <layout-overlay :sceneLayoutSTPModel="sceneLayoutSTPModel" />
      <div
        class="wt-editor-curtains"
        :aspect-ratio="aspectRationName"
        v-if="visibleCurtains"
      >
        <div class="wt-to-snap"></div>
      </div>
    </div>
    <div class="buttons wt2-sg">
      <wt-button
        :disabled="rcp === null"
        type="primary"
        class="pull-right"
        @click="apply"
        :text="'generic.APPLY' | culture"
      />
      <wt-button
        type="transparent"
        class="pull-right"
        @click="close"
        :text="'generic.CANCEL' | culture"
      />
      <wt-button
        type="link"
        class="wt2-color-link"
        @click="reset"
        :text="'generic.RESET' | culture"
      />
    </div>
  </div>
</template>

<script>
import TimelineGatewayController from 'c/TimelineGatewayController';
import RcpControlPanel from 'v/assetModal/RcpControlPanel.vue';
import wtButton from 'components/buttons/wtButton.component.vue';
import {
  NullOffsetScaleHandler,
  AutoCropHandler,
} from '@wochitnpm/adjustasset';
import LayoutOverlay from 'AssetEditor/functionality/Stage/LayoutOverlay.vue';

import { changeScale, aspectRatioGap } from './wtCropping.logic';
import app from 'app';
import RCP from '@wochitnpm/rcp';
import _ from 'lodash-extended';

const getNullCropValue = new NullOffsetScaleHandler();

export default {
  components: {
    wtButton,
    LayoutOverlay,
  },
  computed: {
    innerAspectRatio() {
      return app.ASPECT_RATIOS[app.currentAspectRatio].ratio;
    },
    aspectRationName() {
      return app.currentAspectRatio;
    },
  },
  data() {
    return {
      rcp: null,
      curScale: null,
      dragStart: null,
      dragging: false,
      boundaries: {
        max: {
          top: 0,
          left: 0,
        },
        min: {
          top: 0,
          left: 0,
        },
      },
      assets: [],
      private_selectedAsset: null,
      focusAsset: { name: 'main' },
      layoutCanvas: {
        width: app.ASSET_EDITOR_WIDTH,
        height: app.ASSET_EDITOR_HEIGHT,
      },
      startDragDebounce: _.debounce((e) => {
        // clicking on the rcpPanel cancel start dragging
        if (this.isClickOnRcpPanel(e)) {
          return;
        }
        e.stopPropagation();
        const cropValues = this.cropValues();
        this.dragStart = {
          x: e.pageX,
          y: e.pageY,
          left:
            (cropValues.offset ? cropValues.offset.left : 0) *
            this.layoutCanvas.width,
          top:
            (cropValues.offset ? cropValues.offset.top : 0) *
            this.layoutCanvas.height,
        };
        this.dragging = true;
        window.addEventListener('mousemove', this.onDragging);
      }, app.TIME.MENTAL.λ / 2),
    };
  },
  props: {
    visibleCurtains: {
      type: Boolean,
      default: false,
    },
    disableMain: {
      type: Boolean,
      default: false,
    },
    disableOverlay: {
      type: Boolean,
      default: false,
    },
    currentTime: {
      type: Number,
      default: 0,
    },
    selectedAsset: {
      type: String,
      default: 'main',
      validator: function (value) {
        return ['main', 'overlay'].indexOf(value) !== -1;
      },
    },
    closeModal: { type: Function, required: false, default: _.noop },
    analytics: { type: Function, required: false, default: _.noop },
    main: {
      type: Object,
      required: true,
    },
    overlay: {
      type: Object,
      default: null,
    },
    sendNull: {
      type: Boolean,
      default: false,
    },
    sceneLayoutSTPModel: {
      type: Object,
      default: null,
      required: false,
    },
  },
  watch: {
    dragging(isDragging) {
      const value = isDragging ? 'none' : '';
      if (this.$el.querySelector('.wt2-player-control-panel')) {
        this.$el.querySelector(
          '.wt2-player-control-panel'
        ).style.pointerEvents = value;
      }
      this.$el.querySelector('canvas[for=player]').style.pointerEvents = value;
    },
    focusAsset: {
      handler(asset) {
        if (!asset || !asset.layout) {
          return;
        }
        const layout = asset.layout;
        const width = layout.right - layout.left;
        const height = layout.bottom - layout.top;
        if (width < height) {
          this.layoutCanvas.height = app.ASSET_EDITOR_HEIGHT * height;
          this.layoutCanvas.width =
            this.layoutCanvas.height *
            (app.ASSET_EDITOR_WIDTH / app.ASSET_EDITOR_HEIGHT);
        } else {
          this.layoutCanvas.width = app.ASSET_EDITOR_WIDTH * width;
          this.layoutCanvas.height =
            this.layoutCanvas.width *
            (app.ASSET_EDITOR_HEIGHT / app.ASSET_EDITOR_WIDTH);
        }
      },
      deep: true,
    },
    selectedAsset(selectedAsset) {
      this.setFocusLayout(selectedAsset);
    },
    curScale(scale, oldScale) {
      if (!scale) {
        return;
      }
      let cropValues = this.cropValues();
      if (!cropValues.offset) {
        cropValues = getNullCropValue.AdjustAsset({
          originalRatio: this.focusAsset.metadata,
          innerRatio: {
            width:
              app.ASSET_EDITOR_WIDTH *
              (this.focusAsset.layout.right - this.focusAsset.layout.left),
            height:
              app.ASSET_EDITOR_HEIGHT *
              (this.focusAsset.layout.bottom - this.focusAsset.layout.top),
          },
        });
      }
      this.setBoundaries();
      const changed = changeScale({
        scale,
        oldScale,
        layout: this.focusAsset.layout,
        asset: this.focusAsset.metadata,
        canvas: {
          width: app.ASSET_EDITOR_WIDTH,
          height: app.ASSET_EDITOR_HEIGHT,
        },
        cropValues,
      });

      this.updatePTrackCropAndDraw({
        scale,
        offset: this.validOffset(changed),
      });
    },
  },
  methods: {
    /**
     * check if the mouse event click on this.rcpPanel
     */
    isClickOnRcpPanel(e) {
      return (
        this.rcpPanel &&
        Array.of(...this.rcpPanel.$el.querySelectorAll('*')).find(
          (el) => el === e.toElement
        )
      );
    },
    setFocusLayout(selectedAsset) {
      this.private_selectedAsset = selectedAsset;
      this.updatePTrackCropAndDraw(this.cropValues());
      this.focusAsset =
        this.assets.find((asset) => asset.name === selectedAsset) ||
        this.assets[0];
      this.curScale = null;
      this.$nextTick(() => {
        this.curScale = this.cropValues().scale || 1;
      });
    },
    // called by curScale watcher
    setBoundaries() {
      const layoutRatio = {
        width: this.focusAsset.layout.right - this.focusAsset.layout.left,
        height: this.focusAsset.layout.bottom - this.focusAsset.layout.top,
      };
      const scale = this.curScale || 1;
      this.boundaries = NullOffsetScaleHandler.getMinMax({
        originalRatio: this.focusAsset.metadata,
        innerRatio: {
          width: app.ASSET_EDITOR_WIDTH * layoutRatio.width,
          height: app.ASSET_EDITOR_HEIGHT * layoutRatio.height,
        },
        agd: {
          scale: Number(scale || 1),
        },
      });
    },
    // called by curScale watcher, onDragging method
    validOffset({ top, left }) {
      if (top > this.boundaries.max.top) {
        top = this.boundaries.max.top;
      }
      if (left > this.boundaries.max.left) {
        left = this.boundaries.max.left;
      }
      if (top < this.boundaries.min.top) {
        top = this.boundaries.min.top;
      }
      if (left < this.boundaries.min.left) {
        left = this.boundaries.min.left;
      }
      return {
        top,
        left,
      };
    },
    // called by checkChangeFocus method
    checkIfBeenClick(e, layout) {
      const rcpBox = this.$refs.rcp.getBoundingClientRect();
      const offsetTop = rcpBox.top + rcpBox.height * layout.top;
      const offsetLeft = rcpBox.left + rcpBox.width * layout.left;
      const offsetBottom =
        offsetTop + (layout.bottom - layout.top) * rcpBox.height;
      const offsetRight =
        offsetLeft + (layout.right - layout.left) * rcpBox.width;
      return (
        offsetLeft < e.pageX &&
        offsetRight > e.pageX &&
        offsetTop < e.pageY &&
        offsetBottom > e.pageY
      );
    },
    // called by startDrag, endDrag
    checkChangeFocus(e) {
      if (this.assets < 2) {
        return;
      }
      const clickOn = (
        this.assets.find((asset) => this.checkIfBeenClick(e, asset.layout)) || {
          name: 'main',
        }
      ).name;
      let canCrop;
      if (this.disableMain && clickOn === 'main') {
        canCrop = false;
      } else if (this.disableOverlay && clickOn === 'overlay') {
        canCrop = false;
      } else {
        canCrop = !(
          app.assetType.isWALLPAPER(this[clickOn]) ||
          app.assetType.isWEBPAGE(this[clickOn]) ||
          app.assetType.isMAP(this[clickOn]) ||
          (app.assetType.isTWITTER(this[clickOn]) &&
            !this[clickOn].metadata.url)
        );
      }

      if (!canCrop) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (clickOn !== this.private_selectedAsset) {
        this.setFocusLayout(clickOn);
        return true;
      }
      return false;
    },
    startDrag(e) {
      if (this.checkChangeFocus(e)) {
        return;
      }
      this.startDragDebounce(e);
    },
    endDrag(e) {
      if (!this.dragging) {
        this.checkChangeFocus(e);
        this.startDragDebounce.cancel();
      }
      if (this.overlay || this.isClickOnRcpPanel(e)) {
        return;
      }
      if (!this.dragging && this.rcpPanel) {
        this.rcp.togglePlay();
      }
    },
    scaleGap: function (event) {
      const curScale = parseFloat(this.curScale);
      const gap = parseFloat(event.target.dataset.gap);
      const newScale =
        gap < 0
          ? Math.max(curScale + gap, parseInt(this.$refs.scale.min))
          : Math.min(curScale + gap, parseInt(this.$refs.scale.max));
      if (newScale !== this.curScale) {
        this.curScale = newScale;
        this.trackScale();
      }
    },
    trackScale() {
      if (this.private_selectedAsset === 'main') {
        if (_.isFunction(this.trackScaleChangeMain)) {
          this.trackScaleChangeMain();
        }
      } else {
        if (_.isFunction(this.trackScaleChangeOverlay)) {
          this.trackScaleChangeOverlay();
        }
      }
    },
    trackOffset() {
      if (this.private_selectedAsset === 'main') {
        if (_.isFunction(this.trackOffsetChangeMain)) {
          this.trackOffsetChangeMain();
        }
      } else {
        if (_.isFunction(this.trackOffsetChangeOverlay)) {
          this.trackOffsetChangeOverlay();
        }
      }
    },
    onDragging: function (e) {
      const cropValues = {
        ...this.cropValues(),
        offset: this.validOffset({
          top:
            (e.pageY - this.dragStart.y + this.dragStart.top) /
            this.layoutCanvas.height,
          left:
            (e.pageX - this.dragStart.x + this.dragStart.left) /
            this.layoutCanvas.width,
        }),
      };
      this.updatePTrackCropAndDraw(cropValues);
    },
    onEndDragging: function (e) {
      window.removeEventListener('mousemove', this.onDragging);
      if (this.dragging) {
        this.dragging = false;
        e.stopImmediatePropagation();
        this.trackOffset();
      }
    },
    cropValues(source) {
      const overlays = this.rcp.playList.feed.visual[0].overlayTracks || [];
      switch (source || this.private_selectedAsset) {
        case 'overlay': {
          return this.rcp.playList.feed.visual[0].overlayTracks[0].cropValues;
        }
        default: {
          if (overlays.length > 0 && overlays[0].sceneCropValues) {
            return overlays[0].sceneCropValues;
          }
          return this.rcp.playList.feed.visual[0].cropValues;
        }
      }
    },
    updatePTrackCropAndDraw(cropValues, selectedAssetName) {
      if (!selectedAssetName) {
        selectedAssetName = this.private_selectedAsset;
      }
      const overlays = this.rcp.playList.feed.visual[0].overlayTracks || [];
      switch (selectedAssetName) {
        case 'overlay': {
          this.rcp.playList.feed.visual[0].overlayTracks[0].cropValues = {
            ...cropValues,
          };
          break;
        }
        default: {
          if (overlays.length > 0 && overlays[0].sceneCropValues) {
            this.rcp.playList.feed.visual[0].overlayTracks[0].sceneCropValues =
              {
                ...cropValues,
              };
          }
          this.rcp.playList.feed.visual[0].cropValues = { ...cropValues };
          break;
        }
      }
      this.rcp.movie.channels.visual.draw();
    },
    close() {
      this.closeModal(null, {});
      this.analytics('analytics:assetEditor:crop-close');
    },
    isCropNull({ cropValues, asset }) {
      const nullCropValues = {
        offset: null,
        scale: null,
      };
      if (!cropValues) {
        return nullCropValues;
      }
      if (parseFloat(cropValues.scale) !== 1) {
        return cropValues;
      }
      const { left, top } = getNullCropValue.AdjustAsset({
        originalRatio: asset.metadata,
        innerRatio: {
          width:
            app.ASSET_EDITOR_WIDTH * (asset.layout.right - asset.layout.left),
          height:
            app.ASSET_EDITOR_HEIGHT * (asset.layout.bottom - asset.layout.top),
        },
      }).offset;
      return Math.abs(left - cropValues.offset.left) > 1e-4 ||
        Math.abs(top - cropValues.offset.top) > 1e-4
        ? cropValues
        : nullCropValues;
    },
    apply() {
      let main = this.cropValues('main');
      let overlay = this.overlay ? this.cropValues('overlay') : null;
      if (this.sendNull) {
        const { main: mainAsset, overlay: overlayAsset } = _.reduce(
          this.assets,
          (acc, obj) => ({ ...acc, [obj.name]: obj }),
          {}
        );
        main = this.isCropNull({
          asset: mainAsset,
          cropValues: main,
        });
        if (this.overlay) {
          overlay = this.isCropNull({
            asset: overlayAsset,
            cropValues: overlay,
          });
        }
      }
      this.closeModal(null, {
        main,
        overlay,
        currentTime: this.rcp.getCurrentTime(),
      });
      this.analytics('analytics:assetEditor:crop-apply');
    },
    reset() {
      this.curScale = null;
      this.$nextTick(() => {
        _.each(this.assets, (asset) => {
          if (this.overlay && asset.name === 'main') {
            this.updatePTrackCropAndDraw(this.main.agd, asset.name);
          } else {
            this.updatePTrackCropAndDraw(
              new AutoCropHandler().AdjustAsset({
                originalRatio: {
                  height: asset.metadata.height,
                  width: asset.metadata.width,
                },
                innerRatio: {
                  height: app.ASSET_EDITOR_HEIGHT,
                  width:
                    app.ASSET_EDITOR_HEIGHT *
                    app.ASPECT_RATIOS[app.currentAspectRatio].ratio,
                },
                baseRatio: {
                  height: app.ASSET_EDITOR_HEIGHT,
                  width: app.ASSET_EDITOR_WIDTH,
                },
                agd: {
                  ...asset.agd,
                  offset: null,
                  scale: null,
                },
              }),
              asset.name
            );
          }
        });
        this.curScale = this.cropValues().scale || 1;
      });
      this.analytics('analytics:assetEditor:crop-reset');
    },
    inputScale() {
      this.trackScale();
    },
    setCssVariables({ name, layout }) {
      this.$refs.background.style.setProperty(
        '--z-index-' + name,
        layout.zIndex
      );
      this.$refs.background.style.setProperty(
        '--top-' + name,
        layout.top * 100 + '%'
      );
      this.$refs.background.style.setProperty(
        '--left-' + name,
        layout.left * 100 + '%'
      );
      this.$refs.background.style.setProperty(
        '--height-' + name,
        (layout.bottom - layout.top) * 100 + '%'
      );
      this.$refs.background.style.setProperty(
        '--width-' + name,
        (layout.right - layout.left) * 100 + '%'
      );
    },
    setUpAnalytics() {
      const offsetEventId = this.overlay
        ? 'analytics:assetEditor:crop-offset-changed'
        : 'analytics:assetEditor:crop-offset-changed.primary';
      const scaleEventId = this.overlay
        ? 'analytics:assetEditor:crop-scale-changed'
        : 'analytics:assetEditor:crop-scale-changed.primary';
      this.trackOffsetChangeMain = _.once(() => {
        this.analytics(offsetEventId, { asset: 'main' });
      });
      this.trackOffsetChangeOverlay = _.once(() => {
        this.analytics(offsetEventId, { asset: 'overlay' });
      });
      this.trackScaleChangeMain = _.once(() => {
        this.analytics(scaleEventId, { asset: 'main' });
      });
      this.trackScaleChangeOverlay = _.once(() => {
        this.analytics(scaleEventId, { asset: 'overlay' });
      });
    },
  },
  beforeDestroy() {
    app.keyboardAccessibility.remove(this._uid);
    this.rcp.destroy();
    this.rcpPanel && this.rcpPanel.$destroy();
    this.rcpPanel = null;
    this.rcp = null;
  },
  async mounted() {
    // set main asset at the start of the RCP.
    const main = this.main;

    this.main.displayStartTime = 0;
    const mainMetadata = app.assetType.isWALLPAPER(this.main)
      ? this.layoutCanvas
      : await app.getWidthHeight(this.main.metadata);
    const baseLayout = {
      top: 0,
      left: 0,
      bottom: 1,
      right: 1,
      zIndex: 1,
    };
    const layout = aspectRatioGap(
      this.main.layout || baseLayout,
      this.innerAspectRatio
    );
    this.assets = [
      {
        name: 'main',
        metadata: mainMetadata,
        agd: this.main.agd,
        layout,
      },
    ];
    if (this.overlay) {
      const overlayMetadata = app.assetType.isWALLPAPER(this.overlay)
        ? this.layoutCanvas
        : await app.getWidthHeight(this.overlay.metadata);
      // if there is overlay asset then algin the asset start, end and duration with the overlay.
      this.main.displayDuration = this.overlay.displayDuration;
      this.main.displayEndTime = this.overlay.displayDuration;
      this.main.startTime += this.overlay.displayStartTime;
      this.main.displayStartTime = 0;
      if (this.main.previewResultData) {
        this.main.previewResultData.startTime += this.overlay.displayStartTime;
        this.main.previewResultData.endTime +=
          this.overlay.displayEndTime - this.overlay.displayStartTime;
      }
      this.overlay.displayStartTime = 0;
      this.overlay.displayEndTime = this.overlay.displayDuration;
      const mainLayout = aspectRatioGap(
        app.getMainLayout(this.overlay.sceneLayout),
        this.innerAspectRatio
      );
      const overlayLayout = aspectRatioGap(
        app.getOverlayLayout(this.overlay.sceneLayout),
        this.innerAspectRatio
      );
      this.assets = [
        {
          name: 'main',
          metadata: mainMetadata,
          agd: this.overlay.sceneAgd || this.main.agd,
          layout: mainLayout,
        },
        {
          name: 'overlay',
          metadata: overlayMetadata,
          agd: this.overlay.agd,
          layout: overlayLayout,
        },
      ].sort((a, b) => (a.layout.zIndex > b.layout.zIndex ? -1 : 1));
    } else {
      // there aren't any overlay then set the end of the main asset to his duration
      this.main.displayEndTime = this.main.displayDuration;
    }

    // add global event for mouse up for when the
    // the user finish draging the asset.
    window.addEventListener('mouseup', this.onEndDragging);

    const canPlay =
      (this.main && mainMetadata.url && mainMetadata.url.match(/\.mp4\b/i)) ||
      (this.overlay &&
        this.overlay.metadata.url &&
        this.overlay.metadata.url.match(/\.mp4\b/i));
    // set new rcp
    this.rcp = new RCP({
      showDiagnostic: false,
      el: this.$refs.rcp,
      width: app.ASSET_EDITOR_WIDTH,
      height: app.ASSET_EDITOR_HEIGHT,
      innerAspectRatio: this.visibleCurtains ? 16 / 9 : this.innerAspectRatio,
      canPlay,
    });
    app.setCommonRCPVolume(this.rcp);
    const overlayTracks = this.overlay ? [this.overlay] : [];

    // convert the main asset to visual track.
    const item = TimelineGatewayController.createPlayerVisualTrack({
      // the main asset.
      asset: {
        ...main,
        // apply overlay if exist.
        overlayTracks,
      },
      ignoreRtp: true,
    });

    // append the item to rcp.
    this.rcp.append({ play: false, item });

    // seek to the start of the video.
    this.rcp.seek(this.currentTime);
    const total = this.main.displayDuration;
    this.assets.forEach(({ name, layout }) => {
      this.setCssVariables({ name, layout });
    });
    this.setFocusLayout(this.selectedAsset);
    if (canPlay) {
      this.rcpPanel = new RcpControlPanel({
        el: this.$refs.rcpPanel,
        data() {
          return {
            time: {
              total,
            },
          };
        },
        rcp: this.rcp,
      });
      app.keyboardAccessibility.add(this._uid, [
        {
          on: 'keydown',
          key: 32, // SPACE
          run: () => this.rcp.togglePlay(),
        },
      ]);
    }

    this.setUpAnalytics();
  },
};
</script>

<style lang="scss" scoped>
@import 'style_guide/_sg_variables.scss';
@import 'asset_editor/_mixin.scss';

$stage-zindex: 1;
$hover-color: #1ed5e5;

.background {
  // main variable
  --z-index-main: 0;
  --top-main: 0%;
  --left-main: 0%;
  --height-main: 100%;
  --width-main: 100%;

  // overlay variable
  --z-index-overlay: 0;
  --top-overlay: 0%;
  --left-overlay: 0%;
  --height-overlay: 0%;
  --width-overlay: 0%;

  color: #fff;
  > * {
    background: transparent;
  }
  .scale {
    margin: 0 auto 20px;
    display: flex;
    justify-content: center;
    width: 330px;
    padding: 0 13px;
    height: 30px;
    border-radius: 20px;
    background-color: rgba(255, 255, 255, 0.1);
    input[type='range'] {
      -webkit-appearance: none;
      width: 240px;
      margin: auto 20px;
      background: transparent;
    }
    > span {
      cursor: pointer;
      margin: 8px 0;
      font-size: 14px;
      &:hover {
        color: $color-primary;
      }
    }
    input[type='range' i]::-webkit-slider-container,
    input[type='range' i]::-webkit-media-slider-container {
      height: 30px;
      cursor: pointer;
    }
    input[type='range' i]::-webkit-slider-runnable-track {
      width: 230px;
      height: 2px;
      box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.3);
      border-radius: 2px;
      background-color: #b5b5b5;
    }
    input[type='range']::-webkit-slider-thumb {
      -webkit-appearance: none;
      margin-top: -6px;
      width: 14px;
      height: 14px;
      box-shadow: 0 1px 1px rgba(0, 0, 0, 0.3);
      border: 1px solid rgba(0, 0, 0, 0.12);
      background: #ffffff;
      border-radius: 50px;
    }
  }
  .buttons {
    margin-top: 20px;
    button.transparent {
      &:hover {
        color: #fff;
        background-color: rgba(0, 0, 0, 0.3);
      }
      color: #fff;
    }
    button.primary {
      background-color: #06a9f4;
      &:not(:disabled):hover {
        background-color: #2980b9;
      }
    }
    button.link:hover {
      font-size: 16px;
      font-weight: 500;
    }
  }
  .rcp {
    background: #000;
    width: 688px;
    height: 387px;
    position: relative;
    > canvas,
    div,
    img {
      position: absolute;
    }
    .main,
    .overlay {
      border: 3px solid #fff;
      &.disabled {
        border: none;
      }
      &:hover,
      &.active {
        border: 3px solid $hover-color;
        &.disabled {
          border: none;
        }
      }
      &.active {
        cursor: grab;
        background-size: calc(100% / 3) calc(100% / 3);
        background-image: linear-gradient(
            to right,
            $hover-color 1px,
            transparent 1px
          ),
          linear-gradient(to bottom, $hover-color 1px, transparent 1px);
      }
    }
    .main,
    .overlay {
      cursor: pointer;
      &.disabled {
        cursor: default;
      }
      &:hover {
        border: 3px solid $hover-color;
      }
    }
    .main {
      z-index: calc(1 + var(--z-index-main));
      top: var(--top-main);
      left: var(--left-main);
      height: var(--height-main);
      width: var(--width-main);
    }
    .overlay {
      z-index: calc(1 + var(--z-index-overlay));
      top: var(--top-overlay);
      left: var(--left-overlay);
      height: var(--height-overlay);
      width: var(--width-overlay);
    }
    /deep/ .wt2-player-control-panel {
      z-index: 1000;
      background: rgba(0, 0, 0, 0.7);
    }
  }
  [for='buffer-indicator'] {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 50px;
    width: 50px;
    -webkit-transform: translate(-50%, -50%);
    -ms-transform: translate(-50%, -50%);
    -o-transform: translate(-50%, -50%);
    transform: translate(-50%, -50%);
    z-index: 3;
  }
  .buttons {
    font-size: 16px;
    font-weight: 400;
    .wt2-color-link {
      background: transparent;
      min-width: 0;
      &:hover {
        text-shadow: 0.1px 0.1px #06a9f4;
      }
    }
    .transparent {
      min-width: 0;
    }
  }
  &.dragging {
    cursor: grabbing !important;
    .rcp {
      .main,
      .overlay {
        cursor: grabbing !important;
      }
    }
  }
}

.wt-editor-curtains {
  @include curtains;
}
</style>
